import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Car, Sparkles, Calendar, Clock, User, Mail, Phone, FileText, Check } from "lucide-react";

const GOLD = "#8A8A8A";
const GOLD_LIGHT = "#AAAAAA";

function to12h(t) {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${mStr} ${ampm}`;
}

/* ─────────────────────────────────────────────
   BookingSummary — sticky right-rail summary
───────────────────────────────────────────── */
export default function BookingSummary({ data, currentStep }) {
  const { address, car, condition, service, notes, booking, contact, appointment_date, appointment_time } = data;

  const estimate = useMemo(() => {
    if (booking?.price?.estimate != null) {
      const v = booking.price.estimate;
      return { low: v - 30, high: v + 15 };
    }
    return null;
  }, [booking]);

  const serviceLabel = useMemo(() => {
    const labels = { interior: "Interior Reset", exterior: "Exterior Car Wash", full: "Full Service" };
    return labels[service || "full"] || "Full Service";
  }, [service]);

  const completed = {
    address: !!address,
    car: !!car,
    condition: !!condition,
    service: !!service,
    estimate: !!estimate,
    contact: !!contact?.name && !!contact?.email,
    calendar: !!appointment_date && !!appointment_time,
  };

  const steps = [
    { key: "address",   label: "Where",     num: "01" },
    { key: "car",       label: "Vehicle",   num: "02" },
    { key: "condition", label: "Condition", num: "03" },
    { key: "service",   label: "Service",   num: "04" },
    { key: "estimate",  label: "Estimate",  num: "05" },
    { key: "contact",   label: "Contact",   num: "06" },
    { key: "calendar",  label: "When",      num: "07" },
  ];

  return (
    <motion.aside
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="glass-card h-100 booking-side-panel"
      style={{
        padding: "clamp(20px, 3vw, 28px)",
        position: "relative",
        overflow: "hidden",
      }}
      data-testid="booking-summary-panel"
    >
      {/* gold top accent */}
      <div
        style={{
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: 1,
          background: "linear-gradient(90deg, transparent, rgba(138,138,138,0.6), transparent)",
        }}
      />

      <div className="d-flex align-items-center justify-content-between mb-3">
        <p className="overline mb-0" style={{ color: "rgba(170,170,170,0.85)", fontSize: "10.5px" }}>
          Your booking
        </p>
        <LiveDot />
      </div>

      {/* Live estimate pill */}
      <AnimatePresence mode="wait">
        {estimate ? (
          <motion.div
            key="estimate"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="mb-4"
            style={{
              background: "linear-gradient(135deg, rgba(138,138,138,0.10) 0%, rgba(170,170,170,0.06) 100%)",
              border: "none",
              borderRadius: "16px",
              padding: "16px 18px",
              position: "relative",
              overflow: "hidden",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 32px rgba(0,0,0,0.30)",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "radial-gradient(circle at 100% 0%, rgba(138,138,138,0.12) 0%, transparent 60%)",
                pointerEvents: "none",
              }}
            />
            <p
              className="overline mb-2"
              style={{ color: "rgba(245,245,247,0.45)", fontSize: "9.5px" }}
            >
              Live estimate
            </p>
            <div className="d-flex align-items-baseline gap-2">
              <span
                style={{
                  fontFamily: "monospace",
                  fontSize: "clamp(1.6rem, 4vw, 2.1rem)",
                  fontWeight: 600,
                  letterSpacing: "-0.03em",
                  color: "#f5f5f7",
                  lineHeight: 1,
                }}
              >
                ${estimate.low}
                <span style={{ color: "rgba(245,245,247,0.4)", fontSize: "0.7em" }}> – </span>
                <span style={{ color: GOLD_LIGHT }}>${estimate.high}</span>
              </span>
            </div>
            <p
              className="mb-0 mt-2"
              style={{
                fontSize: "11px",
                color: "rgba(245,245,247,0.5)",
                letterSpacing: "-0.01em",
              }}
            >
              Final after in-person review
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="placeholder"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mb-4"
            style={{
              border: "1px dashed rgba(245,245,247,0.12)",
              borderRadius: "16px",
              padding: "16px 18px",
            }}
          >
            <p
              className="overline mb-2"
              style={{ color: "rgba(245,245,247,0.35)", fontSize: "9.5px" }}
            >
              Estimate
            </p>
            <p
              className="mb-0"
              style={{
                fontSize: "13px",
                color: "rgba(245,245,247,0.45)",
                fontStyle: "italic",
              }}
            >
              Pick a condition to see a price
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Step checklist */}
      <div className="mb-4">
        <p
          className="overline mb-3"
          style={{ color: "rgba(245,245,247,0.40)", fontSize: "10px" }}
        >
          Progress
        </p>
        <div className="d-flex flex-column gap-2">
          {steps.map((s) => {
            const done = completed[s.key];
            const isCurrent = s.key === currentStep;
            return (
              <div
                key={s.key}
                className="d-flex align-items-center gap-2"
                style={{
                  fontSize: "12.5px",
                  letterSpacing: "-0.01em",
                  color: isCurrent
                    ? "#f5f5f7"
                    : done
                    ? "rgba(245,245,247,0.7)"
                    : "rgba(245,245,247,0.32)",
                }}
              >
                <span
                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: done
                      ? "rgba(138,138,138,0.20)"
                      : isCurrent
                      ? "rgba(138,138,138,0.10)"
                      : "rgba(255,255,255,0.04)",
                    border: done
                      ? "1px solid rgba(138,138,138,0.5)"
                      : isCurrent
                      ? "1px solid rgba(138,138,138,0.4)"
                      : "1px solid rgba(255,255,255,0.10)",
                    color: done || isCurrent ? GOLD_LIGHT : "rgba(245,245,247,0.30)",
                  }}
                >
                  {done ? <Check size={10} strokeWidth={2.5} /> : (
                    <span style={{ fontSize: 9, fontFamily: "monospace" }}>{s.num}</span>
                  )}
                </span>
                <span style={{ fontWeight: isCurrent ? 500 : 400 }}>{s.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selections */}
      <div
        className="subtle-divider mb-3"
        style={{ height: 1, background: "linear-gradient(to right, transparent, rgba(245,245,247,0.10), transparent)" }}
      />

      <p
        className="overline mb-3"
        style={{ color: "rgba(245,245,247,0.40)", fontSize: "10px" }}
      >
        Selections
      </p>
      <div className="d-flex flex-column gap-2">
        <Row icon={MapPin}     label="Where"     value={address ? shortAddr(address) : null} />
        <Row icon={Car}        label="Vehicle"   value={car ? `${car.year || ""} ${car.make || ""} ${car.model || ""}`.trim() : null} />
        <Row icon={Sparkles}   label="Condition" value={condition ? titleCase(condition) : null} />
        <Row icon={Check}      label="Service"   value={service ? serviceLabel : null} />
        <Row icon={User}       label="Name"      value={contact?.name} />
        <Row icon={Mail}       label="Email"     value={contact?.email} />
        <Row icon={Phone}      label="Phone"     value={contact?.phone} />
        <Row icon={Calendar}   label="Date"      value={appointment_date || null} />
        <Row icon={Clock}      label="Time"      value={to12h(appointment_time) || null} />
        {notes ? <Row icon={FileText} label="Notes" value={truncate(notes, 36)} /> : null}
      </div>
    </motion.aside>
  );
}

function Row({ icon: Icon, label, value }) {
  const has = !!value;
  return (
    <motion.div
      initial={false}
      animate={{ opacity: has ? 1 : 0.4 }}
      transition={{ duration: 0.3 }}
      className="d-flex align-items-start gap-2"
      style={{ fontSize: "12.5px", letterSpacing: "-0.01em" }}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
        style={{
          width: 24,
          height: 24,
          borderRadius: 7,
          background: has ? "rgba(138,138,138,0.10)" : "rgba(255,255,255,0.03)",
          color: has ? GOLD_LIGHT : "rgba(245,245,247,0.30)",
          marginTop: 1,
        }}
      >
        <Icon size={11} strokeWidth={1.6} />
      </span>
      <div className="d-flex flex-column" style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            fontSize: "9.5px",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "rgba(245,245,247,0.40)",
            fontFamily: "monospace",
            lineHeight: 1.1,
          }}
        >
          {label}
        </span>
        <span
          style={{
            color: has ? "rgba(245,245,247,0.85)" : "rgba(245,245,247,0.30)",
            fontStyle: has ? "normal" : "italic",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.3,
          }}
          title={value || ""}
        >
          {value || "Not set"}
        </span>
      </div>
    </motion.div>
  );
}

function LiveDot() {
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <span
        className="rounded-circle d-inline-block"
        style={{
          width: 6,
          height: 6,
          background: GOLD,
          animation: "pulse-glow 2.4s ease-in-out infinite",
        }}
      />
      <span
        style={{
          fontFamily: "monospace",
          fontSize: "9.5px",
          letterSpacing: "0.18em",
          color: "rgba(170,170,170,0.7)",
          textTransform: "uppercase",
        }}
      >
        Live
      </span>
    </span>
  );
}

function shortAddr(a) {
  if (!a) return null;
  const line1 = a.line1 || a.formatted_address || a.street || "";
  const city = a.city || "";
  if (!line1) return null;
  return city ? `${line1}, ${city}` : line1;
}

function titleCase(s) {
  if (!s) return null;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function truncate(s, n) {
  if (!s) return null;
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
